import React from 'react';
import { FiArrowUp, FiArrowDown } from 'react-icons/fi';
import styles from './HighlightStatCard.module.scss';

/**
 * Standalone version of the "highlight" stat card pattern used inline by
 * DashboardOverviewNew's KeyHighlights (User Dashboard / Department
 * Dashboard both render it — TeamDashboard imports DashboardOverviewNew's
 * own module.scss directly). That pattern isn't a shared component — it's
 * page-local markup styled by DashboardOverviewNew.module.scss's
 * .highlightItem/.highlightIconWrapper/.highlightValue/etc classes — so this
 * recreates the same recipe (white card, shadow, no border, icon in a
 * colored circle, value + trend arrow) as one, so any other page needing
 * this exact look doesn't have to duplicate it a second time.
 *
 * Deliberately plain — no tinted background, no sparkline, no uppercase
 * caption — matching that recipe exactly rather than TrendStatCard's
 * different one (built for App Usage / URL Tracking's trend-over-time
 * widgets, not this card family).
 */
export const HighlightStatCardComponent = ({
  icon,
  iconBg,
  label,
  value,
  tooltip,
  trendPct,
  // Whether the arrow's direction is the good outcome — e.g. "up" is bad
  // for a lateness count. Defaults to 'positive' (up = good = green).
  trendSentiment = 'positive',
  // '%' for a percentage delta (the common case); pass '' for a raw count
  // delta (e.g. "+2" critical delays, not "+2%").
  trendSuffix = '%',
  isLoading = false,
}) => {
  const hasTrend = trendPct != null;
  const isUp = hasTrend && trendPct >= 0;
  const isGood = trendSentiment === 'positive' ? isUp : !isUp;

  return (
    <div
      className={styles.card}
      style={isLoading ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
    >
      <div className={styles.header}>
        <div className={styles.iconWrapper} style={{ backgroundColor: iconBg }}>
          {icon}
        </div>
        <span className={styles.label} title={tooltip}>
          {label}
        </span>
      </div>

      <div className={styles.valueRow}>
        <span className={styles.value}>{value}</span>
        {hasTrend && (
          <span
            className={`${styles.trend} ${isGood ? styles.trendUp : styles.trendDown}`}
          >
            {isUp ? <FiArrowUp size={10} /> : <FiArrowDown size={10} />}
            {Math.abs(trendPct)}
            {trendSuffix}
          </span>
        )}
      </div>

      {hasTrend && <span className={styles.trendVs}>vs yesterday</span>}
    </div>
  );
};

HighlightStatCardComponent.displayName = 'HighlightStatCard';
